/**
 * Initial state configuration for the Conference Room Designer.
 * @module initialState
 */

/**
 * Generates the initial state for a conference room area.
 * @function
 * @param {Object} state - The current state (unused in this function).
 * @returns {Object} An object representing the initial state of a conference room area.
 */

const initialState = {
  /**
   * Creates an initial area object for a conference room.
   * @function
   * @param {Object} state - The current state (unused in this function).
   * @returns {Object} An object containing the initial properties for a conference room area.
   */
  area: (state) => {
    return ({
      id: "",
      name: "",
      areaType: "room",
      dimensions: { length: 6.096, width: 4.2672 },
      location: { left: 0, top: 0 },
      ceilingHeight: { min: 2.8956, max: 2.8956 },

      /**
       * areaOrientation is not currently used for other than the UI. This might change or deleted
       */
      areaOrientation: 'lengthwise',

      // Door default configuration
      door: {
        side: 3,
        scale: [1, 1],
        angle: 0,
        x: null,
        y: null,
      },

      // Ceiling tiles default configuration
      ceilingTiles: {
        tile: "no_ceiling_tiles",
        qty: { x: 0, y: 0 },
        transform: { x: 0, y: 0, a: 0 },
        checked: []
      },

      // Table default configuration
      table: {
        shape: null,
        size: { length: 0, width: 0 },
        transform: { x: 0, y: 0, a: 0 },
        ghostChairs: [],
      },

      // Room acoustics default configuration
      acoustics: 'good',

      // Microphones default configuration
      microphones: {
        model: 'tcm_x',
        modelName: 'Parlé TCM-X',
        coverage: {
          full_room: true,
          focused: false,
        },
        height: 2.8956, // starts at ceiling height by default
        maxHeight: 4.0284, // default value at good acoustics
        micDistanceLegendMax: 6, // default value at given init state
        micMaxRadius: 2.4755, // default value at given init state
        coverageDensity: {
          value: 1,
          jumpPoints: [],
          overlaps: []
        },
        qty: 0,
        layout: []
      },

      // Parle bar flag
      hasBar: false,

      // Speakers default configuration
      speakers: {
        mountingType: 'ceiling',  // Default to ceiling mounting
        model: 'ceilingspkr',
        modelName: 'Desono C-IC6',
        // Must agree with `constants.equipment.speakers[model].coverage(area)`, which resolves to
        // `{ focused: !!table.shape, full_room: !table.shape }`. A new area starts with
        // `table.shape: null` (below), so the correct seed is full_room — there is nothing to focus
        // on yet. This used to be hardcoded `focused: true`, and nothing recomputed it until an
        // action routed through `resetDevices` (logic.js), so a brand-new design ran its FIRST
        // speaker layout against the table-less focused box: 70% of the room dimensions carrying
        // `spkrAdj: 1` (getCoverageBoxes in roomCalculations.ts). getSpeakerAxisCount subtracts that
        // 1 from each axis, collapsing the default 20'x14' room's 2x1 grid to a single speaker,
        // which then landed on the same centre point as the lone ceiling mic and got shoved
        // diagonally by the mic/speaker collision snap. Changing the mic type "fixed" it only
        // because that path calls resetDevices, which re-derives coverage from the equipment fn.
        // Adding a table later still flips this to focused via setTable -> resetArea -> resetDevices.
        coverage: {
          full_room: true,
          focused: false,
        },
        height: 2.8956,
        coverageDensity: {
          value: 1,
          jumpPoints: [],
          overlaps: []
        },
        qty: 0,
        layout: [],
        linkedSpeakers: true,
        targetPoint: { x: 0, y: 0 },
        showAimingAngles: false
      },

      // Cameras default configuration
      cameras: {
        model: 'vidi280',
        modelName: 'Vidi 280',
        coverageRadius: 5,
        coverageAngle: 56.5,
        hasCamera: true,
        plotVisibility: {
          wide_only: true,
          zoom_only: false
        }, // Default to show wide-angle plot for dual-imager cameras
        qty: 0,  // Changed to 0 to match empty layout - will be recalculated
        layout: []
      },

      // Other room properties
      productFamily: 'Tesira',
      ucCompute: 'None',
      // Platform: 'Windows' | 'Android' | null. Drives the UC Compute → Platform
      // → UC device cascade selector is gated on UC Compute being set. Android implies the Tree Frog (UCX)
      // path; Windows uses the existing Tesira / Devio SCX path with UCC-Lenovo.
      platform: null,
      equipmentList: [],

      // note: temporary flags - don't remove just yet
      recalculate: false,
      remap: false,

      // Expansion microphone state (for bar mics with coverage gap)
      expansionMicRecommended: false,
      expansionMicType: null, // 'ceiling' | 'table' | 'none' | null (null = dialog not yet shown)
      expansionMicCoverage: null, // { focused: bool, full_room: bool } | null
      expansionMicrophones: null, // { model, modelName, qty, layout, height? } | null — `height` is only
      // set for ceiling expansion mics (dcm_x); defaults to flush ceiling height on creation
      // (setExpansionMicType/placeExpansionMicAtLocation) — NOT the primary mic's height, since
      // the primary is always a non-CBC bar mounted at table/wall height, not ceiling height.
      // User-editable afterward via the "Height" dimensionInput (same component as the primary
      // ceiling mic's Height field).
      // True when the user opted into expansion mics via the menu's "Add" button despite
      // the gap-detection algorithm not recommending them. Switches the calc to standard
      // placement and clears the coverage toggle to "neither selected".
      expansionMicManualOptIn: false,
      // True when the expansion placement RAN AND DECLINED because the remaining coverage gap is
      // below the minimum worth a mic (spec edge case B / below-minimum-viable Scenario 3). The
      // type stays 'ceiling'/'table' — the config is still meaningful and the placement restores
      // the mics once acoustics move back to a tier with a worthwhile gap — but the mic selector
      // offers "Add" rather than a "Remove" for mics that aren't there. Distinct from a merely
      // empty layout, which also describes the transient right after a type change.
      expansionMicSuppressed: false,

      // Expansion speaker state (for bar + expansion ceiling speaker configurations)
      expansionSpeakerRecommended: false, // set by calculateBarSpeakerCoverageGap when bar leaves a meaningful speaker gap
      expansionSpeakerType: null, // 'ceiling' | 'pendant' | 'none' | null (null = dialog not yet shown)
      expansionSpeakerCoverage: null, // { focused: bool, full_room: bool } | null — ceiling-only; pendant doesn't use coverage modes
      expansionSpeakers: null, // { model, modelName, qty, layout } | null

      // Customization flags
      customized: {
        microphones: false,
        speakers: false,
        cameras: false,
        expansionMicrophones: false,
        expansionSpeakers: false
      },

      collisions: {
        microphones: [],
        speakers: []
      },


    })
  }
};

export default initialState;
